import { useEffect, useState } from 'react';
import { Checkbox, CheckboxGroup, Fieldset, Icon } from '@chakra-ui/react';
import { FaCircleInfo } from 'react-icons/fa6';
import { Tooltip } from '@/components/ui/tooltip';
import { layers } from '@/geospatial';
import { useMap } from '@/components/map/map-context';
import { storageManager } from '@/lib/utils/storage-manager';

export const LayerSelection = () => {
  const { layerGroup, map } = useMap();
  const [selectedLayer, setSelectedLayer] = useState<string[]>(() => {
    return storageManager.loadLayers();
  });
  
  useEffect(() => {
    if (layerGroup && map) {
      const updateLayerVisibility = () => {
        const groupLayers = layerGroup.getLayers().getArray();
        const zoom = map.getView().getZoom() || 0;
        
        groupLayers.forEach((layer) => {
          const layerMetadata = layers.find((l) => l.layer === layer);
          if (layerMetadata) {
            const isSelected = selectedLayer.includes(layerMetadata.value);
            
            if (layerMetadata.minVis !== undefined && layerMetadata.maxVis !== undefined) {
              // Layer has zoom constraints
              const zoomVisible = zoom >= layerMetadata.minVis && zoom <= layerMetadata.maxVis;
              layer.setVisible(isSelected && zoomVisible);
            } else {
              // Layer has no zoom constraints
              layer.setVisible(isSelected);
            }
          }
        });
      };

      // Initial update
      updateLayerVisibility();

      // Listen for zoom changes
      const view = map.getView();
      view.on('change:resolution', updateLayerVisibility);

      return () => {
        view.un('change:resolution', updateLayerVisibility);
      };
    }
  }, [layerGroup, selectedLayer, map]);

  const handleChange = (value: string[]) => {
    if (value) {
      setSelectedLayer(value);
      storageManager.saveLayers(value);
    }
  };

  return (
    <Fieldset.Root>
      <CheckboxGroup defaultValue={selectedLayer} name='layer' onValueChange={handleChange}>
        <Fieldset.Legend fontSize='sm' mb='2'>
          Layers:
        </Fieldset.Legend>
        <Fieldset.Content gap='2' pl={4}>
          {layers.map((item) => (
            <Checkbox.Root key={item.value} size='xs' value={item.value}>
              <Checkbox.HiddenInput />
              <Checkbox.Control>
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Label>
                {item.name}
                {item.desc && (
                  <Tooltip content={item.desc}>
                    <Icon as={FaCircleInfo} ml={1} boxSize={3} />
                  </Tooltip>
                )}
              </Checkbox.Label>
            </Checkbox.Root>
          ))}
        </Fieldset.Content>
      </CheckboxGroup>
    </Fieldset.Root>
  );
};
